import type {
  ListStationsResponse,
  StationWeekList
} from '@/Network/Types/Responses/StationResponses'
import type { LensColorBy } from '@/Network/Types/Requests/DashboardSavedViewRequests'
import {
  CHECK_COLORS,
  NO_VALUE_COLOR,
  OPEN_STATION_COLORS,
  TEAM_LENS_COLORS,
  categoricalColor,
  sectorColorKey,
  specialtyColorKey,
  type CheckColorKey
} from '@/utils/lensColors'
import { catalogDisplayName } from '@/utils/stationDisplay'
import { UNIDENTIFIED_COVERAGE_LABEL, isUnidentifiedCoverage } from '@/utils/coverage'
import { UNIDENTIFIED_COVERAGE_LEGEND_SWATCH } from '@/components/designSystem/Table/StandardRow/unidentifiedCoverageStyles'

export type LegendItem = { key: string; label: string; color: string }

function forEachCard(
  data: ListStationsResponse | undefined,
  fn: (card: StationWeekList) => void
): void {
  ;(data?.data ?? []).forEach((company) =>
    company.healthUnities.forEach((unit) =>
      (unit.schedules ?? []).forEach((schedule) =>
        schedule.shifts.forEach((shift) =>
          shift.hours.forEach((hour) =>
            hour.positions.forEach((position) =>
              position.stations.forEach((day) => day.forEach(fn))
            )
          )
        )
      )
    )
  )
}

/** Algum card da resposta está "Ocupado sem identificação". */
export function hasUnidentifiedCard(data: ListStationsResponse | undefined): boolean {
  let found = false
  forEachCard(data, (card) => {
    if (!found && isUnidentifiedCoverage(card)) found = true
  })
  return found
}

/**
 * Legenda V1 (grupo e modal): a entrada "Ocupado s/ ident." só aparece quando a resposta já
 * conhece o estado — traz `coverageCounts` ou algum card UNIDENTIFIED. BE antigo: legenda de hoje.
 */
export function showUnidentifiedLegend(data: ListStationsResponse | undefined): boolean {
  return Boolean(data?.coverageCounts) || hasUnidentifiedCard(data)
}

/**
 * Chaves de cor de setor e de especialidade presentes na lista (inclui vaga aberta: o ponto
 * de setor/especialidade do card também usa a cor). Alimenta `setCategoricalColorGroups`.
 */
export function collectCategoricalKeys(data: ListStationsResponse | undefined): {
  sector: string[]
  specialty: string[]
} {
  const sector = new Set<string>()
  const specialty = new Set<string>()
  forEachCard(data, (card) => {
    const sectorKey = sectorColorKey(card)
    const specialtyKey = specialtyColorKey(card)
    if (sectorKey) sector.add(sectorKey)
    if (specialtyKey) specialty.add(specialtyKey)
  })
  return { sector: [...sector], specialty: [...specialty] }
}

/** Legenda das lentes de cor (spec §5.2). "Tipo" mantém a legenda de hoje; vaga aberta tem cor própria (paleta). */
export function buildLegendItems(
  data: ListStationsResponse | undefined,
  colorBy: LensColorBy
): LegendItem[] {
  if (colorBy === 'tipo') return []
  const open: LegendItem = { key: 'open', label: 'Vaga aberta', color: OPEN_STATION_COLORS.stripe }
  // "Ocupado sem identificação" só entra quando a resposta traz o estado (BE antigo: legenda de hoje).
  const hasUnidentified = hasUnidentifiedCard(data)
  const tail: LegendItem[] = [
    ...(hasUnidentified
      ? [
          {
            key: 'unidentified',
            label: UNIDENTIFIED_COVERAGE_LABEL,
            color: UNIDENTIFIED_COVERAGE_LEGEND_SWATCH
          }
        ]
      : []),
    open
  ]
  if (colorBy === 'equipe') {
    return [
      { key: 'ours', label: 'Nossa equipe', color: TEAM_LENS_COLORS.ours.stripe },
      { key: 'others', label: 'Outra equipe', color: TEAM_LENS_COLORS.others.stripe },
      ...tail
    ]
  }
  if (colorBy === 'check') {
    return [
      ...(Object.keys(CHECK_COLORS) as CheckColorKey[]).map((key) => ({
        key,
        label: CHECK_COLORS[key].label,
        color: CHECK_COLORS[key].color
      })),
      ...tail
    ]
  }
  const seen = new Map<string, LegendItem>()
  let hasNone = false
  forEachCard(data, (card) => {
    // Vaga aberta e ocupado s/ ident. têm cor própria: não viram entrada de setor/especialidade.
    if (isUnidentifiedCoverage(card) || card.opened) return
    const key = colorBy === 'setor' ? sectorColorKey(card) : specialtyColorKey(card)
    if (!key) {
      hasNone = true
      return
    }
    if (!seen.has(key)) {
      const ref = colorBy === 'setor' ? card.sector : card.specialty
      seen.set(key, { key, label: catalogDisplayName(ref ?? null), color: categoricalColor(key) })
    }
  })
  const items = [...seen.values()].sort((a, b) =>
    a.label.localeCompare(b.label, 'pt-BR', { sensitivity: 'base' })
  )
  if (hasNone) {
    items.push({
      key: 'none',
      label: colorBy === 'setor' ? 'Sem setor' : 'Sem especialidade',
      color: NO_VALUE_COLOR
    })
  }
  return [...items, ...tail]
}
